import { Permission } from '@Constants/Permission.g';
import { useHasPermission } from '@Contexts/appContext';
import { useInvoicePrintPreview, usePrintOptions } from '@Hooks/Documents';
import { usePrepareEmail } from '@Hooks/Mailing';
import { useCanCollectOrder } from '@Hooks/Orders';
import { useOrderAttachments } from '@Hooks/Orders/useOrderAttachments';
import { type ApiOrder, EnumProcessState, type SendDeliverySlipRequest, type SendPrepareInvoiceRequest } from '@Model';
import { EditFrameMode, EnumStatus, type IEditFrame, useModalState } from '@synqo/frontend-component-library';

// @ai-generated 2026-10-22T10:53:17+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useOrderActions(frame: IEditFrame<ApiOrder>, staging: boolean) {
    const order = frame.data;
    const hasPermission = useHasPermission();

    const isInViewMode = frame.mode === EditFrameMode.View;
    const canEditOrder = hasPermission(Permission.AuftragsbearbeitungOrdersUpdate);

    const {
        collectible,
        canCollect,
        canApprove,
        actionText: collectionActionText,
        tooltip: collectionTooltip,
    } = useCanCollectOrder(order);

    const cashPaymentModal = useModalState();

    const deliverySlipEmail = usePrepareEmail<SendDeliverySlipRequest>('/prepare_delivery_slip_email');
    const invoiceEmail = usePrepareEmail<SendPrepareInvoiceRequest>('/prepare_invoice_email');

    const printOptionsInfo = usePrintOptions('order', order.id, isInViewMode && order.status !== EnumStatus.Draft);
    const previewInvoice = useInvoicePrintPreview(printOptionsInfo);

    const attachmentsInfo = useOrderAttachments(order.id);
    const canManageAttachments = isInViewMode && hasPermission(Permission.AuftragsbearbeitungOrdersAttachmentsManage);

    const approveOrder = async () => {
        const onSave = frame.events.onSave;
        if (onSave) {
            const saveResult = await onSave();
            if (!saveResult) {
                return false;
            }
        }

        return frame.requestUpdateState((latest, saveData) =>
            saveData({ ...latest, process_state: EnumProcessState.Approved }),
        );
    };

    return {
        staging,
        canDelete: !staging && order.process_state !== EnumProcessState.Completed,
        canApprove: canEditOrder && canApprove && !staging,
        canCollect,
        isCollectible: canEditOrder && collectible,
        collectionActionText,
        collectionTooltip,

        canPrint: !staging,
        canReadAttachments: hasPermission(Permission.AuftragsbearbeitungOrdersAttachmentsRead),
        canManageAttachments,
        canDownloadAttachments: isInViewMode,
        attachmentsInfo,

        printOptionsInfo,
        previewInvoice,
        deliverySlipEmail,
        invoiceEmail,

        cashPaymentModal,

        approveOrder,
    };
}

export type OrderActions = ReturnType<typeof useOrderActions>;
